import { useEffect, useState } from 'react';
import axios from 'axios';
import { Film, Clapperboard, UserRound, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRenderPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/render-status`, getAuthHeader()).then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const content = !data ? (
    <div className="flex items-center gap-2 text-espresso/60 p-8" data-testid="render-status-loading">
      <Loader2 className="w-5 h-5 animate-spin" /> Lade Render-Status…
    </div>
  ) : (
    <div className="space-y-6" data-testid="render-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-espresso">Video-Produktion</h1>
        <p className="text-sm text-espresso/60 mt-1">Aktualisiert sich alle 30 Sekunden automatisch</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Film, label: 'Videos fertig', value: `${data.videos_done} / ${data.total_modules}`, testid: 'stat-done' },
          { icon: Clapperboard, label: 'davon Kino-Stil', value: data.kino_style, testid: 'stat-kino' },
          { icon: UserRound, label: 'mit Julia', value: data.julia_versions, testid: 'stat-julia' },
          { icon: Loader2, label: 'gerade in Arbeit', value: data.active_jobs.length, testid: 'stat-active' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-cream-200 p-4" data-testid={s.testid}>
            <s.icon className="w-5 h-5 text-terra mb-2" />
            <div className="text-2xl font-bold text-espresso">{s.value}</div>
            <div className="text-xs text-espresso/60">{s.label}</div>
          </div>
        ))}
      </div>

      {data.active_jobs.length > 0 && (
        <div className="bg-white rounded-2xl border border-cream-200 p-5" data-testid="active-jobs">
          <h2 className="font-semibold text-espresso mb-3 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-terra" /> Läuft gerade
          </h2>
          {data.active_jobs.map((j, i) => (
            <div key={i} className="text-sm text-espresso/80 py-1" data-testid={`active-job-${i}`}>
              {j.type === 'julia' ? '🎤 Julia-Montage' : '🎬 Kino-Render'}: <span className="font-medium">{j.module_id}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-cream-200 p-5">
        <h2 className="font-semibold text-espresso mb-3">Fortschritt pro Kurs</h2>
        <div className="space-y-3">
          {data.courses.map((c) => (
            <div key={c.course_id} data-testid={`course-row-${c.course_id}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-espresso/90 truncate pr-2">{c.title}</span>
                <span className="text-espresso/60 whitespace-nowrap">
                  {c.done}/{c.modules}{c.julia > 0 && ` · ${c.julia}× Julia`}
                  {c.done === c.modules && <CheckCircle2 className="w-4 h-4 text-green-600 inline ml-1 -mt-0.5" />}
                </span>
              </div>
              <div className="h-2 bg-cream-100 rounded-full overflow-hidden">
                <div className="h-full bg-terra rounded-full transition-all" style={{ width: `${c.modules ? (100 * c.done) / c.modules : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-cream-200 p-5" data-testid="recent-renders">
        <h2 className="font-semibold text-espresso mb-3">Zuletzt fertiggestellt</h2>
        {data.recent.map((r) => (
          <div key={r.name} className="flex justify-between text-sm py-1 border-b border-cream-100 last:border-0">
            <span className="text-espresso/80 truncate pr-2">{r.name}</span>
            <span className="text-espresso/50 whitespace-nowrap">{r.at}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
}
